import {
	createContext,
	FC,
	PropsWithChildren,
	useCallback,
	useContext,
	useMemo,
} from 'react';
import { NavigateOptions, useNavigate } from 'react-router-dom';

import { useFeatureFlag } from '@mopla/business-logic';
import {
	BOOKING_NUMBER_QUERY_PARAM,
	TICKET_QUERY_PARAM,
	TicketsStep,
} from '@mopla/constants';

export interface INavigateToTicketsParams {
	bookingNumber?: string;
	step?: TicketsStep;
}

interface IPassengerRoutingHelpersContextType {
	navigateToSubscriptionFlow(options?: NavigateOptions): void;
	navigateToResumeSubscriptionFlow: VoidFunction;
	navigateToSubscriptionManager: VoidFunction;
	navigateToPaymentManager: VoidFunction;
	navigateToSearch(options?: NavigateOptions): void;
	navigateToDTicketScreen(options?: NavigateOptions): void;
	navigateToDTicketAlreadyExistScreen(options?: NavigateOptions): void;
	navigateToTickets(
		params?: INavigateToTicketsParams,
		options?: NavigateOptions
	): void;
}

const PassengerRoutingHelpersContext =
	createContext<IPassengerRoutingHelpersContextType>(
		{} as IPassengerRoutingHelpersContextType
	);

export enum EPassengerRouteParams {
	DticketDetails = 'dticket_details',
	DticketAlreadyExist = 'dticket_already-exist',
	DticketSubscribe = 'dticket_subscribe',
	DticketResume = 'dticket_resume',
	PaymentDetails = 'paymentDetails',
	// this query param is still used as a plain string (not as the enum) in the codebase
	SubscriptionManager = 'subscription=pause',
}

export const usePassengerRoutingHelpers = () => {
	const ctx = useContext(PassengerRoutingHelpersContext);
	if (!ctx) {
		throw new Error(
			'usePassengerRoutingHelpers must be used within WithPassengerRoutingHelpers'
		);
	}
	return ctx;
};

export const WithPassengerRoutingHelpers: FC<PropsWithChildren> = ({
	children,
}) => {
	const navigate = useNavigate();
	const { isNewActivitiesEnabled } = useFeatureFlag();

	/* TODO: redesign-dticket - switch to navigateToUserDTicket everywhere, then delete/mark navigateToDTicketScreen as deprecated */
	const navigateToDTicketScreen = useCallback(
		(options?: NavigateOptions) => {
			navigate(
				{ pathname: '/home', search: EPassengerRouteParams.DticketDetails },
				options
			);
		},
		[navigate]
	);

	const navigateToDTicketAlreadyExistScreen = useCallback(
		(options?: NavigateOptions) => {
			navigate(
				{
					pathname: '/home',
					search: EPassengerRouteParams.DticketAlreadyExist,
				},
				options
			);
		},
		[navigate]
	);

	const navigateToSubscriptionFlow = useCallback(() => {
		navigate(
			{ pathname: '/home', search: EPassengerRouteParams.DticketSubscribe },
			{ replace: true }
		);
	}, [navigate]);

	const navigateToResumeSubscriptionFlow = useCallback(() => {
		navigate({
			pathname: '/home',
			search: EPassengerRouteParams.DticketResume,
		});
	}, [navigate]);

	const navigateToSubscriptionManager = useCallback(() => {
		navigate({
			pathname: '/home/profile',
			search: EPassengerRouteParams.SubscriptionManager,
		});
	}, [navigate]);

	const navigateToPaymentManager = useCallback(() => {
		navigate({
			pathname: '/home/profile',
			search: EPassengerRouteParams.PaymentDetails,
		});
	}, [navigate]);

	const navigateToSearch = useCallback(
		(options?: NavigateOptions) => {
			navigate({ pathname: '/home/search' }, options);
		},
		[navigate]
	);

	const navigateToTickets = useCallback(
		(params?: INavigateToTicketsParams, options?: NavigateOptions) => {
			const { bookingNumber, step } = params ?? {};
			const isActivities = isNewActivitiesEnabled();

			if (!bookingNumber) {
				navigate(
					{ pathname: isActivities ? '/home/activities' : '/home/tickets' },
					options
				);
				return;
			}

			/** QR is legacy-only, TicketsManager keeps serving it */
			if (step === TicketsStep.qr) {
				navigate(
					{
						pathname: '/home/tickets',
						search: `${TICKET_QUERY_PARAM}=${bookingNumber}&step=${TicketsStep.qr}`,
					},
					options
				);
				return;
			}

			/** The activities detail opens on the booking number alone; the legacy one needs its step */
			if (isActivities) {
				navigate(
					{
						pathname: '/home/activities',
						search: `${BOOKING_NUMBER_QUERY_PARAM}=${bookingNumber}`,
					},
					options
				);
				return;
			}

			navigate(
				{
					pathname: '/home/tickets',
					search: `${TICKET_QUERY_PARAM}=${bookingNumber}&step=${TicketsStep.details}`,
				},
				options
			);
		},
		[navigate, isNewActivitiesEnabled]
	);

	const value = useMemo<IPassengerRoutingHelpersContextType>(
		() => ({
			navigateToSubscriptionFlow,
			navigateToResumeSubscriptionFlow,
			navigateToSubscriptionManager,
			navigateToPaymentManager,
			navigateToSearch,
			navigateToDTicketScreen,
			navigateToDTicketAlreadyExistScreen,
			navigateToTickets,
		}),
		[
			navigateToSubscriptionFlow,
			navigateToResumeSubscriptionFlow,
			navigateToSubscriptionManager,
			navigateToPaymentManager,
			navigateToSearch,
			navigateToDTicketScreen,
			navigateToDTicketAlreadyExistScreen,
			navigateToTickets,
		]
	);

	return (
		<PassengerRoutingHelpersContext.Provider value={value}>
			{children}
		</PassengerRoutingHelpersContext.Provider>
	);
};
